import React from 'react';
import {
  Activity, BarChart3, BookOpenCheck, BriefcaseBusiness, Building2, ChevronRight, FileStack, Gauge, Import, KeyRound, Languages, LogOut, Menu, RefreshCw, Settings2, SlidersHorizontal, User, UserCog, Users, X,
} from 'lucide-react';
import { Avatar, Badge, IconButton } from './ui.jsx';
import { classNames, formatTaskMessage } from '../utils.js';

const NAVIGATION = [
  { id: 'overview', label: '工作台', icon: Gauge, roles: ['admin', 'hr', 'hiring_manager'] },
  { id: 'candidates', label: '候选人', icon: Users, roles: ['admin', 'hr', 'hiring_manager'] },
  { id: 'import', label: '导入与评估', icon: Import, roles: ['admin', 'hr', 'hiring_manager'] },
  { id: 'jobs', label: '岗位管理', icon: BriefcaseBusiness, roles: ['admin', 'hr', 'hiring_manager'] },
  { id: 'departments', label: '部门管理', icon: Building2, roles: ['admin'] },
  { id: 'calibration', label: '人工校准', icon: SlidersHorizontal, roles: ['admin', 'hiring_manager'] },
  { id: 'talent-library', label: '人才标准', icon: BookOpenCheck, roles: ['admin', 'hr', 'hiring_manager'] },
];

const PAGE_LABELS = Object.fromEntries([
  ...NAVIGATION.map((item) => [item.id, item.label]),
  ['models', '模型与处理'], ['audit', '审计记录'], ['accounts', '账户与权限'], ['candidate', '候选人档案'],
]);

function Sidebar({ page, routeJobId, navigate, mobileOpen, setMobileOpen, workspace, currentUser, jobs }) {
  const navigation = NAVIGATION.filter((item) => item.roles.includes(currentUser?.role));
  const assignedJobs = currentUser?.role === 'hiring_manager'
    ? jobs.filter((job) => currentUser.jobIds?.includes(job.id)) : [];
  return (
    <>
      {mobileOpen && <div className="sidebar-scrim" onClick={() => setMobileOpen(false)} />}
      <aside className={classNames('sidebar', mobileOpen && 'sidebar-open')}>
        <div className="brand-row">
          <div className="brand-lockup">
            <img className="brand-logo" src="/brand/hinomi-logo-black-horizontal.png" alt="Hinomi" />
            <span>Talent Intelligence</span>
          </div>
          <IconButton className="mobile-sidebar-close" icon={X} label="关闭导航" onClick={() => setMobileOpen(false)} />
        </div>
        <nav className="main-nav" aria-label="主导航">
          <span className="nav-label">工作台</span>
          {navigation.map((item) => (
            <button
              key={item.id}
              className={classNames(page === item.id && 'active')}
              onClick={() => { navigate(item.id); setMobileOpen(false); }}
            >
              <item.icon size={18} /><span>{item.label}</span>
              {item.id === 'candidates' && <Badge tone="neutral">{workspace?.candidateCount || 0}</Badge>}
            </button>
          ))}
          {assignedJobs.length > 0 && <>
            <span className="nav-label nav-label-system">负责岗位</span>
            {assignedJobs.map((job) => <button key={job.id} className={classNames(page === 'candidates' && routeJobId === job.id && 'active')} onClick={() => { navigate('candidates', job.id); setMobileOpen(false); }}><BriefcaseBusiness size={17} /><span>{job.title}</span><Badge tone="neutral">{job.candidateCount || 0}</Badge></button>)}
          </>}
          <span className="nav-label nav-label-system">系统</span>
          {currentUser?.role === 'admin' && <button className={classNames(page === 'accounts' && 'active')} onClick={() => { navigate('accounts'); setMobileOpen(false); }}>
            <UserCog size={18} /><span>账户与权限</span>
          </button>}
          {currentUser?.role === 'admin' && <button className={classNames(page === 'models' && 'active')} onClick={() => { navigate('models'); setMobileOpen(false); }}>
            <Settings2 size={18} /><span>模型与处理</span>
          </button>}
          {currentUser?.role === 'admin' && <button className={classNames(page === 'audit' && 'active')} onClick={() => { navigate('audit'); setMobileOpen(false); }}>
            <Activity size={18} /><span>审计记录</span>
          </button>}
        </nav>
      </aside>
    </>
  );
}

export function AppShell({ children, state, page, routeJobId, navigate, onLogout, onSecurity, onProfile, taskOpen, setTaskOpen, mobileOpen, setMobileOpen, locale, setLocale }) {
  const activeTasks = state?.tasks?.filter((task) => ['queued', 'processing'].includes(task.status)) || [];
  const currentUser = state?.currentUser;
  const candidate = page === 'candidate' ? state?.candidates?.find((item) => window.location.hash.endsWith(item.id)) : null;
  return (
    <div className="app-shell">
      <Sidebar
        page={page}
        routeJobId={routeJobId}
        navigate={navigate}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        workspace={{ candidateCount: state?.dashboard?.candidateCount }}
        currentUser={currentUser}
        jobs={state?.jobs || []}
      />
      <div className="workspace-shell">
        <header className="topbar">
          <div className="topbar-left">
            <IconButton className="mobile-menu" icon={Menu} label="打开导航" onClick={() => setMobileOpen(true)} />
            <div className="topbar-context"><strong>{PAGE_LABELS[page] || '招聘工作台'}</strong>{candidate && <><ChevronRight size={14} /><span>{candidate.displayName}</span></>}</div>
          </div>
          <div className="topbar-actions">
            <button
              className="locale-switch"
              data-i18n-ignore
              aria-label={locale === 'en' ? 'Switch to Chinese' : '切换到英文'}
              title={locale === 'en' ? '切换到中文' : 'Switch to English'}
              onClick={() => setLocale(locale === 'en' ? 'zh-CN' : 'en')}
            >
              <Languages size={16} /><span>{locale === 'en' ? '中文' : 'English'}</span>
            </button>
            <button className="task-trigger" aria-label="任务" onClick={() => setTaskOpen(!taskOpen)}>
              <FileStack size={17} />
              <span>任务</span>
              {activeTasks.length > 0 && <i>{activeTasks.length}</i>}
            </button>
            <button className="quick-import" aria-label="导入" onClick={() => navigate('import')}>
              <Import size={16} /><span>导入</span>
            </button>
            <div className="signed-in-account">
              <Avatar name={currentUser?.name || ''} avatarUrl={currentUser?.avatarUrl} size="sm" />
              <span><strong>{currentUser?.name || '未登录'}</strong><small>{state?.workflow?.roleLabels?.[currentUser?.role] || ''}</small></span>
              <IconButton icon={User} label="个人资料" onClick={onProfile} />
              <IconButton icon={KeyRound} label="修改密码" onClick={onSecurity} />
              <IconButton icon={LogOut} label="退出登录" onClick={onLogout} />
            </div>
          </div>
        </header>
        <main className="workspace-content">{children}</main>
      </div>
    </div>
  );
}

export function TaskDrawer({ open, tasks = [], onClose, onRetry, onCancel }) {
  const [retryingId, setRetryingId] = React.useState('');
  const [cancellingId, setCancellingId] = React.useState('');
  const activeCandidateIds = new Set(tasks
    .filter((task) => ['queued', 'processing'].includes(task.status) && task.candidateId)
    .map((task) => task.candidateId));
  const latestTaskByCandidate = new Map();
  for (const task of tasks) {
    if (!task.candidateId) continue;
    const latest = latestTaskByCandidate.get(task.candidateId);
    if (!latest || String(task.createdAt) > String(latest.createdAt)) latestTaskByCandidate.set(task.candidateId, task);
  }
  const visibleTasks = tasks.filter((task) => !task.candidateId || latestTaskByCandidate.get(task.candidateId)?.id === task.id);
  const retry = async (id) => {
    if (retryingId) return;
    setRetryingId(id);
    try { await onRetry(id); } finally { setRetryingId(''); }
  };
  const cancel = async (id) => {
    if (cancellingId) return;
    setCancellingId(id);
    try { await onCancel(id); } finally { setCancellingId(''); }
  };
  return (
    <>
      {open && <div className="drawer-scrim" onClick={onClose} />}
      <aside className={classNames('task-drawer', open && 'task-drawer-open')} aria-hidden={!open}>
        <header><div><span>处理中心</span><h2>后台任务</h2></div><IconButton icon={X} label="关闭" onClick={onClose} /></header>
        <div className="task-summary">
          <div><strong>{visibleTasks.filter((task) => task.status === 'processing').length}</strong><span>处理中</span></div>
          <div><strong>{visibleTasks.filter((task) => task.status === 'queued').length}</strong><span>已排队</span></div>
          <div><strong>{visibleTasks.filter((task) => task.status === 'failed').length}</strong><span>失败</span></div>
        </div>
        <div className="task-list">
          {visibleTasks.length === 0 && <div className="drawer-empty"><BarChart3 size={22} /><p>当前没有处理任务</p></div>}
          {visibleTasks.map((task) => {
            const canRetry = task.status === 'failed'
              && (!task.candidateId || (!activeCandidateIds.has(task.candidateId) && latestTaskByCandidate.get(task.candidateId)?.id === task.id));
            const canCancel = ['queued', 'processing'].includes(task.status);
            return (
            <article className="task-item" key={task.id}>
              <div className="task-item-head"><strong>{task.candidateName || '批量任务'}</strong>{task.type === 'resume_translation' && <Badge tone="neutral">简历翻译</Badge>}<Badge tone={task.status === 'failed' ? 'danger' : task.status === 'completed' ? 'success' : task.status === 'cancelled' ? 'neutral' : 'info'}>{task.status === 'processing' ? '处理中' : task.status === 'queued' ? '已排队' : task.status === 'failed' ? '失败' : task.status === 'cancelled' ? '已取消' : '完成'}</Badge></div>
              <p>{formatTaskMessage(task.message) || task.stage}</p>
              <div className="task-progress"><i style={{ width: `${task.progress}%` }} /></div>
              <div className="task-meta"><span>{task.jobTitle}</span><strong>{task.progress}%</strong></div>
              <div className="task-item-actions">
                {canCancel && <button className="task-cancel-btn" disabled={Boolean(cancellingId)} onClick={() => cancel(task.id)}>{cancellingId === task.id ? '正在取消' : '取消任务'}</button>}
                {canRetry && <button className="task-retry-btn" disabled={Boolean(retryingId)} onClick={() => retry(task.id)}><RefreshCw size={12} />{retryingId === task.id ? '正在重新排队' : '重试任务'}</button>}
              </div>
            </article>
            );
          })}
        </div>
      </aside>
    </>
  );
}
